
import React from 'react';
import { useApp } from '../../context/AppContext';
import { ShieldAlert, ArrowRight, Upload, Wallet, QrCode, MapPin } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const Dashboard: React.FC = () => {
  const { currentUser, setCurrentScreen, transactions } = useApp();
  const { toast } = useToast();

  const pendingTransactions = transactions.filter(tx => tx.status === 'pending' || tx.status === 'confirmed');
  const completedTransactions = transactions.filter(tx => tx.status === 'completed');

  const handleStartKyc = () => {
    setCurrentScreen('kyc');
  };

  const handleCreateTransaction = () => {
    if (currentUser?.kycStatus !== 'approved') {
      toast({
        title: "KYC Required",
        description: "Please complete identity verification first",
        variant: "destructive"
      });
      setCurrentScreen('kyc');
    } else {
      setCurrentScreen('transaction');
    }
  };

  return (
    <div className="flex flex-col h-full pb-20">
      <div className="bg-gradient-to-r from-neobank-primary to-neobank-secondary text-white p-6 rounded-3xl shadow-md mb-6">
        <h1 className="text-2xl font-bold mb-2">Welcome back!</h1>
        <p className="opacity-80 mb-4">{currentUser?.email}</p>
      </div>

      {currentUser?.kycStatus !== 'approved' && (
        <div className="mobile-card flex items-center">
          <div className="bg-amber-100 p-3 rounded-full mr-4">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium">Identity Verification</h3>
            <p className="text-sm text-gray-600 mb-2">
              {currentUser?.kycStatus === 'not_started' 
                ? 'Complete KYC to unlock all features' 
                : currentUser?.kycStatus === 'pending' 
                  ? 'Your KYC is under review' 
                  : 'Your KYC was rejected. Please try again.'}
            </p>
            {(currentUser?.kycStatus === 'not_started' || currentUser?.kycStatus === 'rejected') && (
              <button 
                onClick={handleStartKyc} 
                className="text-sm text-neobank-primary font-medium flex items-center"
              >
                Start Verification <ArrowRight className="ml-1 w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="px-4 mb-4">
        <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-4">
          <button 
            onClick={handleCreateTransaction} 
            className="bg-white p-4 rounded-xl shadow border border-gray-100 flex flex-col items-center"
          >
            <div className="bg-neobank-lightAccent p-3 rounded-full mb-2">
              <Upload className="w-6 h-6 text-neobank-primary" />
            </div>
            <span className="text-sm font-medium">Convert Crypto</span>
          </button>
          
          <button 
            onClick={() => setCurrentScreen('history')} 
            className="bg-white p-4 rounded-xl shadow border border-gray-100 flex flex-col items-center"
          >
            <div className="bg-neobank-lightAccent p-3 rounded-full mb-2">
              <Wallet className="w-6 h-6 text-neobank-primary" />
            </div>
            <span className="text-sm font-medium">Transaction History</span>
          </button>
          
          <button 
            onClick={() => setCurrentScreen('atm-map')} 
            className="bg-white p-4 rounded-xl shadow border border-gray-100 flex flex-col items-center"
          >
            <div className="bg-neobank-lightAccent p-3 rounded-full mb-2">
              <MapPin className="w-6 h-6 text-neobank-primary" />
            </div>
            <span className="text-sm font-medium">ATM Locations</span>
          </button>
          
          {pendingTransactions.length > 0 && (
            <button 
              onClick={() => {
                if (pendingTransactions.find(tx => tx.status === 'confirmed')) {
                  setCurrentScreen('atm-qr');
                } else {
                  setCurrentScreen('transaction-confirmation');
                }
              }} 
              className="bg-white p-4 rounded-xl shadow border border-gray-100 flex flex-col items-center"
            >
              <div className="bg-amber-100 p-3 rounded-full mb-2">
                <QrCode className="w-6 h-6 text-amber-600" />
              </div>
              <span className="text-sm font-medium">Pending Transaction</span>
            </button>
          )}
        </div>
      </div>

      {completedTransactions.length > 0 && (
        <div className="px-4">
          <h2 className="text-xl font-bold mb-2">Recent Transactions</h2>
          <div className="mobile-card divide-y">
            {completedTransactions.slice(0, 3).map(tx => (
              <div key={tx.id} className="py-3 flex justify-between items-center">
                <div>
                  <p className="font-medium">{tx.amount} USD</p>
                  <p className="text-sm text-gray-500">{new Date(tx.date).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{tx.cryptoAmount} {tx.cryptoCurrency}</p>
                  <p className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 inline-block">
                    Completed
                  </p>
                </div>
              </div>
            ))}
            {completedTransactions.length > 3 && (
              <button 
                onClick={() => setCurrentScreen('history')} 
                className="py-2 w-full text-neobank-primary font-medium flex items-center justify-center"
              >
                View All <ArrowRight className="ml-1 w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
